import React, { useState } from 'react';
import './RestaurantCart.css';
import { useMultiCart } from '../../Context/MultiCartContext';
import ImageComponent from '../common/Image';
import { useNavigate } from 'react-router-dom';
import ConfirmationDialog from '../common/ConfirmationDialog';
import { useTranslation } from 'react-i18next';

// Utility function to ensure consistent string keys for React
const getStringKey = (id, fallback = '') => {
    if (!id) return fallback;
    if (typeof id === 'string') return id;
    
    // Handle populated MongoDB documents
    if (typeof id === 'object' && id._id) {
        return id._id.toString();
    }
    
    if (id.toString && typeof id.toString === 'function') return id.toString();
    return String(id) || fallback;
};

const RestaurantCart = ({ cart, onCheckout, onAddMoreItems, onShareCart, simple = false }) => {
    const { t } = useTranslation();
    const { 
        removeFromRestaurantCart, 
        addToRestaurantCart,
        clearRestaurantCart, 
        formatImageUrl,
        saveCart,
        error,
        setError
    } = useMultiCart();
    
    const [loading, setLoading] = useState(false);
    const [showClearCartDialog, setShowClearCartDialog] = useState(false);
    const navigate = useNavigate();

    const handleRemoveItem = async (itemId) => {
        try {
            setLoading(true);
            // Extract the actual ID string from the object
            const foodId = getStringKey(itemId);
            const restaurantId = getStringKey(cart.restaurantId);
            const success = await removeFromRestaurantCart(foodId, restaurantId);
            if (!success) {
                setError('Failed to remove item from cart');
            }
        } catch (err) {
            console.error('Error removing item:', err);
            setError('Failed to remove item from cart');
        } finally {
            setLoading(false);
        }
    };

    const handleAddItem = async (itemId) => {
        try {
            setLoading(true);
            // Extract the actual ID string from the object
            const foodId = getStringKey(itemId);
            const restaurantId = getStringKey(cart.restaurantId);
            const metaItem = cart.items.find(i => getStringKey(i.foodId) === foodId);
            const success = await addToRestaurantCart(foodId, restaurantId, {
                name: metaItem?.name,
                price: metaItem?.price,
                image: metaItem?.image,
                restaurantName: cart.restaurantName,
                restaurantImage: cart.restaurantImage
            });
            if (!success) {
                setError('Failed to add item to cart');
            }
        } catch (err) {
            console.error('Error adding item:', err);
            setError('Failed to add item to cart');
        } finally {
            setLoading(false);
        }
    };

    const handleClearCart = async () => {
        setShowClearCartDialog(true);
    };

    const confirmClearCart = async () => {
        try {
            setLoading(true);
            // Extract the actual ID string from the object
            const restaurantId = getStringKey(cart.restaurantId);
            const success = await clearRestaurantCart(restaurantId);
            if (!success) {
                setError('Failed to clear cart');
            }
        } catch (err) {
            console.error('Error clearing cart:', err);
            setError('Failed to clear cart');
        } finally {
            setLoading(false);
            setShowClearCartDialog(false);
        }
    };

    const handleShareClick = async () => {
        try {
            setLoading(true);
            const restaurantId = getStringKey(cart.restaurantId);
            
            // First, save the cart with public sharing enabled
            const cartName = `${cart.restaurantName} Cart`;
            const description = `Shared cart from ${cart.restaurantName} with ${cart.items?.length || 0} items`;
            
            const saveResult = await saveCart(restaurantId, cartName, description, true);
            
            if (!saveResult || !saveResult.shareId) {
                setError('Failed to create shareable cart');
                return;
            }

            // Create the proper share URL pointing to our SharedCart component
            const shareUrl = `${window.location.origin}/#/cart/shared/${saveResult.shareId}`;
            
            const shareData = {
                        title: `${cart.restaurantName} Cart - DASHBASKET`,
        text: `Check out my ${cart.restaurantName} cart on DASHBASKET! Join me to add more items.`,
                url: shareUrl
            };

            // Try native sharing first, fallback to clipboard
            if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
                await navigator.share(shareData);
            } else {
                // Fallback: copy to clipboard if native share not available
                await navigator.clipboard.writeText(shareUrl);
                alert('Cart link copied to clipboard! Share it with your friends.');
            }
        } catch (error) {
            console.error('Error sharing cart:', error);
            // If user cancels share, don't show error
            if (error.name !== 'AbortError') {
                // Final fallback: manual copy
                try {
                    const restaurantId = getStringKey(cart.restaurantId);
                    // Fallback to simple restaurant sharing if cart sharing fails
                    const fallbackUrl = `${window.location.origin}/#/restaurant/${restaurantId}`;
                    await navigator.clipboard.writeText(fallbackUrl);
                    alert('Cart link copied to clipboard! (Note: This links to the restaurant page)');
                } catch (copyError) {
                    setError('Failed to share cart. Please try again.');
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const handleAddMoreItems = (restaurantId) => {
        // Extract the actual ID string from the object
        const actualRestaurantId = getStringKey(restaurantId);
        navigate(`/restaurant/${actualRestaurantId}`);
    };

    const handleCheckout = (cart) => {
        // Navigate to the place order page with cart data
        navigate('/order-details', { state: { restaurantCart: cart } });
    };

    return (
        <>
            <div className="restaurant-cart-simple">
                {error && (
                    <div className="error-message">
                        {error}
                        <button onClick={() => setError(null)} className="close-error">×</button>
                    </div>
                )}
                
                {/* First Row: Restaurant name and logo */}
                <div className="cart-header">
                    <div className="restaurant-logo">
                        <ImageComponent 
                            src={formatImageUrl(cart.restaurantImage) || formatImageUrl(cart.restaurantId?.branding?.logo)}
                            alt={cart.restaurantName}
                            width={40}
                            height={40}
                            fallback={`data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40"><circle cx="20" cy="20" r="20" fill="%23ff6b47"/><text x="20" y="27" font-family="Arial" font-size="16" text-anchor="middle" fill="white" font-weight="bold">${cart.restaurantName?.charAt(0) || 'R'}</text></svg>`}
                        />
                    </div>
                    
                    <div className="restaurant-info">
                        <h3 className="restaurant-name">{cart.restaurantName}</h3>
                    </div>

                    <div className="cart-actions-row">
                    <div className="cart-actions">
                        <button 
                            className="share-btn"
                            onClick={handleShareClick}
                            title={t('cart.share_cart')}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z" fill="currentColor"/>
                            </svg>
                        </button>
                        <button 
                            className="delete-cart-btn" 
                            onClick={handleClearCart}
                            title={t('cart.remove_cart_title')}
                        >
                            🗑️
                        </button>
                    </div>
                </div>
                </div>       

                {/* Second Row: Food items with images, names, and quantity controls */}
                <div className="food-items-list">
                    {cart.items.map((item, index) => {
                        const itemKey = getStringKey(item.foodId, `item-${index}`);
                        
                        return (
                            <div key={itemKey} className="cart-food-item">
                                <div className="cart-food-item-image">
                                    <ImageComponent 
                                        src={formatImageUrl(item.image)}
                                        alt={item.name}
                                        width={32}
                                        height={32}
                                    />
                                </div>
                                <div className="cart-food-item-content">
                                    <div className="cart-food-item-info">
                                        <span className="cart-food-item-name">{item.name}</span>
                                    </div>
                                    <div className="quantity-controls">
                                        <button 
                                            className="quantity-btn minus"
                                            onClick={() => handleRemoveItem(item.foodId)}
                                            disabled={loading}
                                        >
                                            {loading ? '...' : '−'}
                                        </button>
                                        <span className="quantity">{item.quantity}</span>
                                        <button 
                                            className="quantity-btn plus"
                                            onClick={() => handleAddItem(item.foodId)}
                                            disabled={loading}
                                        >
                                            {loading ? '...' : '+'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Third Row: Checkout and Add more items buttons */}
                <div className="cart-actions-bottom">
                    <button 
                        className="checkout-btn"
                        onClick={() => handleCheckout(cart)}
                    >
                        {t('cart.checkout')}
                    </button>
                    <button 
                        className="add-more-btn"
                        onClick={() => handleAddMoreItems(cart.restaurantId)}
                    >
                        {t('cart.add_more_items')}
                    </button>
                </div>
            </div>

            {/* Confirmation Dialog for Clearing Cart */}
            <ConfirmationDialog
                isOpen={showClearCartDialog}
                onClose={() => setShowClearCartDialog(false)}
                onConfirm={confirmClearCart}
                title={t('cart.remove_cart_title')}
                message={t('cart.remove_cart_message', { restaurantName: cart.restaurantName })}
                confirmText={t('cart.remove_cart_confirm')}
                cancelText={t('cart.remove_cart_cancel')}
                confirmButtonClass="btn-danger"
                cancelButtonClass="btn-secondary"
                isLoading={loading}
                loadingText={t('cart.removing_cart')}
            />
        </>
    );
};

export default RestaurantCart; 